import { useEffect, useMemo, useRef, useState } from "react";
import { TestsNotTakingField } from "@/components/app/profile/TestsNotTakingField";
import { FitWeightsField } from "@/components/app/profile/FitWeightsField";
import { Link } from "react-router-dom";
import { UnsavedChangesGuard } from "@/components/app/profile/UnsavedChangesGuard";
import { useProfileDraft, DraftBanner } from "@/components/app/profile/ProfileDraft";
import { CheckCircle2, Circle, ArrowLeft, Save, AlertTriangle, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PredictedGradesPicker from "@/components/app/profile/PredictedGradesPicker";
import SubjectChipPicker from "@/components/app/profile/SubjectChipPicker";
import MetroTicket from "@/components/app/MetroTicket";
import CareerPicker from "@/components/app/CareerPicker";
import { useCurrency } from "@/context/CurrencyContext";
import { COUNTRIES, COUNTRY_LABEL } from "@/lib/countries";

/**
 * Full-form student profile — richer/faster editor than the inline
 * completeness widget on `/shortlist`. Groups fields by section so
 * students can jump to whichever chunk they want to fill.
 *
 * Phase-1 scope
 * -------------
 * - Reads/writes `db.profiles` via `/api/profile` (existing endpoint;
 *   already patch-safe via `exclude_unset`).
 * - Renders progress bar + per-section completeness pulled from
 *   `/api/profile/completeness`.
 * - No consent / export flows yet — those land in Phase 2 alongside
 *   Fast Apply.
 */
const SECTIONS = [
  {
    key: "basics", title: "The basics",
    subtitle: "The three things every match needs to know.",
    fields: [
      { key: "year_of_entry",  label: "Year you plan to start",     type: "number",  placeholder: "e.g. 2027" },
      { key: "curriculum",     label: "Curriculum you're taking",   type: "select", options: [
        { value: "a_level",    label: "A-Level (UK)" },
        { value: "ib",         label: "International Baccalaureate" },
        { value: "us-hs",      label: "US High School" },
        { value: "cbse",       label: "CBSE (India)" },
        { value: "isc",        label: "ISC (India)" },
        { value: "atar",       label: "ATAR (Australia)" },
        { value: "abitur",     label: "Abitur (Germany)" },
        { value: "other",      label: "Other" },
      ]},
      { key: "intended_degree", label: "What do you want to study?", type: "subject_chips",
        placeholder: "Start typing a subject — e.g. Dance, Film…",
        hint: "Pick from the suggestions so matching works. Unknown subjects won't filter the search." },
    ],
  },
  {
    key: "grades", title: "Predicted grades",
    subtitle: "Add each subject with its predicted grade — pick your qualification, type any subject, and add up to 5.",
    fields: [
      { key: "predicted_grades",      label: "Your subjects", type: "predicted_grades" },
      { key: "ib",                    label: "IB total (predicted)",    type: "number", placeholder: "38" },
      { key: "sat",                   label: "SAT total",               type: "number", placeholder: "1450" },
      { key: "act",                   label: "ACT composite",           type: "number", placeholder: "32" },
      { key: "tests_not_taking",      label: "Tests I'm not taking",    type: "tests_not_taking" },
    ],
  },
  {
    key: "fee_status", title: "Fee status",
    subtitle: "What rate you'd pay at each country's universities. Final determination is made by the university — see our methodology for the rules.",
    fields: [
      { key: "nationalities",       label: "Your passport(s)", type: "country_chips",
        placeholder: "Start typing — e.g. United Kingdom",
        hint: "Dual nationals: add every passport you hold." },
      { key: "residence_country",   label: "Country you live in now",
        type: "country_select", placeholder: "Pick a country" },
      { key: "residence_since_year",label: "Living there since (year)",
        type: "number", placeholder: "2019",
        hint: "3+ years of ordinary residence is the key threshold in the UK and Ireland." },
      { key: "euss_status",         label: "UK EU Settlement Scheme status (if applicable)",
        type: "select", options: [
          { value: "n/a",         label: "Not applicable" },
          { value: "settled",     label: "Settled status" },
          { value: "pre_settled", label: "Pre-settled status" },
          { value: "none",        label: "No EUSS status" },
        ],
        hint: "Post-Brexit, EEA/Swiss nationals need settled or pre-settled status for UK home fees." },
      { key: "uk_eea_resident_2020", label: "UK national living in EEA/Switzerland on 31 Dec 2020?",
        type: "boolean",
        hint: "Opens a transitional UK home-fee pathway (window closes 1 Jan 2028)." },
      { key: "fee_status_preview",  label: "Live preview", type: "fee_status_preview" },
    ],
  },
  {
    key: "budget", title: "Budget & funding",
    subtitle: "Roughly what tuition budget can your family stretch to?",
    fields: [
      { key: "max_annual_tuition_usd",   label: "Max annual tuition",         type: "currency_optional" },
      { key: "annual_living_budget_usd", label: "Annual living budget",       type: "currency_optional" },
      { key: "scholarship_required",     label: "Do you need a scholarship?", type: "scholarship_gate" },
      { key: "funds_source",             label: "Funding source",             type: "select", options: [
        { value: "family",     label: "Family savings" },
        { value: "loan",       label: "Loan" },
        { value: "scholarship",label: "Scholarship" },
        { value: "mixed",      label: "A mix" },
      ]},
    ],
  },
  {
    key: "goals", title: "Career direction",
    subtitle: "What do you want the degree to help you become?",
    fields: [
      { key: "career_target",      label: "Your single-word destination", type: "career_picker",
        placeholder: "Start typing: Dentist, Software Engineer, Dancer…",
        hint: "Pick a canonical occupation — powers your journey ticket and Fast Match boosts." },
      { key: "career_direction",   label: "In one sentence",   type: "textarea",
        ai: "career_direction",
        placeholder: "e.g. A machine-learning engineer at a climate startup" },
      { key: "why_this_subject",   label: "Why this subject?",  type: "textarea",
        ai: "why_this_subject",
        placeholder: "Early draft of your personal statement — we'll help refine it later." },
      { key: "dream_employers",    label: "Dream employers or grad schools",
        type: "chips", placeholder: "Add — hit Enter" },
    ],
  },
  {
    key: "preferences", title: "Study preferences",
    subtitle: "Everything defaulted to 3 means \"no preference\".",
    fields: [
      { key: "campus_pref",              label: "Campus type",              type: "select", options: [
        { value: "either", label: "No preference" },
        { value: "urban",  label: "Urban / city" },
        { value: "campus", label: "Traditional campus" },
      ]},
      { key: "settlement_intent",        label: "Long-term settlement",     type: "select", options: [
        { value: "unsure", label: "Unsure" },
        { value: "stay",   label: "I want to stay after graduating" },
        { value: "leave",  label: "I want to return home" },
      ]},
      { key: "exam_vs_coursework_pref",  label: "Exams (1) vs Coursework (5)",  type: "slider" },
      { key: "teaching_size_pref",       label: "Big lectures (1) vs Small tutorials (5)", type: "slider" },
      { key: "workload_intensity_pref",  label: "Balance (1) vs Intense (5)",   type: "slider" },
      { key: "curriculum_structure_pref",label: "Structured (1) vs Pick-your-own (5)", type: "slider" },
    ],
  },
  {
    key: "what_matters", title: "What matters to me",
    subtitle: "Optional. Only the answers you give are used; untouched sliders stay as defaults.",
    fields: [
      { key: "fit_weights", label: "How much each of these matters", type: "fit_weights" },
      { key: "plays_sport", label: "Do you play sport?", type: "boolean" },
      { key: "sports", label: "Which sports? (optional)", type: "chips", placeholder: "e.g. Rowing — hit Enter", showIf: (p) => p.plays_sport === true },
      { key: "sport_level", label: "Level (optional)", type: "select", showIf: (p) => p.plays_sport === true, options: [
        { value: "", label: "—" },
        { value: "recreational", label: "Recreational" },
        { value: "club", label: "Club" },
        { value: "county_regional", label: "County / regional" },
        { value: "national_plus", label: "National or above" },
      ]},
    ],
  },
  {
    key: "identity", title: "About you",
    subtitle: "Voluntary — nothing is shared with any university without your explicit consent. Nationality and country of residence live in the Fee status section above.",
    fields: [
      { key: "first_name",   label: "First name",   type: "text" },
      { key: "last_name",    label: "Last name",    type: "text" },
      { key: "date_of_birth",label: "Date of birth",type: "date" },
    ],
  },
];

function SaveBar({ message, saving, dirtyCount, onSave, tid, msgTid }) {
  return (
    <div className="flex items-center gap-4">
      {message && (
        <span
          data-testid={msgTid}
          className={`text-[12px] font-medium ${message.kind === "ok" ? "text-emerald-700" : "text-rose-700"}`}
        >
          {message.text}
        </span>
      )}
      <button
        type="button"
        onClick={onSave}
        disabled={saving || !dirtyCount}
        data-testid={tid}
        className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white text-sm font-medium px-3 py-1.5 rounded-md transition-colors"
      >
        <Save className="w-4 h-4" />
        {saving ? "Saving…" : dirtyCount ? `Save ${dirtyCount} change${dirtyCount > 1 ? "s" : ""}` : "Saved"}
      </button>
    </div>
  );
}

export default function StudentProfilePage() {
  const [profile,  setProfile]  = useState({});
  const [dirty,    setDirty]    = useState({});
  const [touchedAxes, setTouchedAxes] = useState(() => new Set());
  const [meta,     setMeta]     = useState(null);
  const [ticket,   setTicket]   = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [message,  setMessage]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [authGate, setAuthGate] = useState(false);
  const [consentModal, setConsentModal] = useState(null); // {age, dobPending}
  const [parentEmail, setParentEmail]   = useState("");
  const [consentStatus, setConsentStatus] = useState(null); // "sending" | "sent" | "failed" | "granted"
  const [consentError, setConsentError]   = useState(null);
  const { openSignIn, user } = useAuth();
  // Typed-but-not-added entries (chip text, a subject+grade row) register a committer so Save counts and includes them.
  const committers = useRef({});
  const [pendingValues, setPendingValues] = useState({});
  const pendingKeys = Object.keys(pendingValues);
  const setPending = (key, commit, preview) => {
    if (commit) committers.current[key] = commit; else delete committers.current[key];
    setPendingValues(pv => {
      if (!commit && !(key in pv)) return pv;
      const next = { ...pv };
      if (commit) next[key] = preview; else delete next[key];
      return next;
    });
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, c, t] = await Promise.all([
        // Each call swallows its own rejection and resolves to a
        // sentinel so a stale session (401) doesn't bubble up as an
        // unhandled promise rejection — those trigger Webpack's dev-
        // server error overlay, which then intercepts every click on
        // the page (including inside the subject-chip picker).
        api.get("/profile").catch((err) => ({ data: null, _err: err })),
        api.get("/profile/completeness").catch(() => ({ data: null })),
        api.get("/ticket/me").catch(() => ({ data: null })),
      ]);
      if (p._err && p._err.response && p._err.response.status === 401) {
        setAuthGate(true);
        return;
      }
      setProfile(p.data || {});
      setMeta(c.data);
      setTicket(t.data || null);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  // On arriving with a `#profile-field-...` hash (e.g. from the
  // shortlist budget tile), wait for the form to render, then scroll
  // to and focus the target field. `scroll-mt-24` on each label keeps
  // the header out of the way; focus briefly highlights the input so
  // the user sees what they came for.
  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;

    const focusAndScroll = () => {
      const el = document.getElementById(hash);
      if (!el) return;
      const input = el.querySelector("input, select, textarea");
      if (input) input.focus({ preventScroll: true });
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    // Some profile sections (notably fee-status preview) hydrate after
    // the initial render and can shift lower fields down the page. Re-run
    // the anchor scroll once after that layout settles so deep links still
    // leave the intended field visible and focused.
    const timers = [50, 700].map((delay) => setTimeout(focusAndScroll, delay));
    return () => timers.forEach((timer) => clearTimeout(timer));
  }, [loading]);

  const setField = (k, v, axis) => {
    setDirty(d => ({ ...d, [k]: v }));
    setProfile(p => ({ ...p, [k]: v }));
    if (axis) setTouchedAxes(s => new Set(s).add(axis));
  };

  const draftChanges = useMemo(() => ({ ...dirty, ...pendingValues }), [dirty, pendingValues]);
  const draftAxes = useMemo(() => [...touchedAxes], [touchedAxes]);
  const { draft, discard: discardDraft } = useProfileDraft({
    uid: user?.user_id, ready: !loading && !authGate, profile, changes: draftChanges, axes: draftAxes,
  });
  const restoreDraft = () => {
    const ownKeys = new Set(Object.keys(draftChanges));
    Object.entries(draft.changes).forEach(([k, v]) => { if (!ownKeys.has(k)) setField(k, v); });
    setTouchedAxes(s => new Set([...s, ...(draft.axes || [])]));
    discardDraft();
  };

  const save = async () => {
    let ok = false;
    // Give any focused input a chance to blur-commit (country-chips
    // input holds its own un-committed state — this flushes it into
    // `dirty` before we POST).
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
    const extra = Object.fromEntries(Object.entries(committers.current).map(([k, commit]) => [k, commit()]));
    committers.current = {};
    setPendingValues({});
    const changes = { ...dirty, ...extra };
    if (!Object.keys(changes).length) return true;
    if (Object.keys(extra).length) setProfile(p => ({ ...p, ...extra }));
    setSaving(true); setMessage(null);
    try {
      // fit_weights provenance is per axis: only sliders the student moved or confirmed.
      const axes = [...touchedAxes].map((a) => `fit_weights.${a}`);
      await api.post("/profile", {
        ...changes,
        explicit_fields: [...Object.keys(changes).filter((k) => k !== "fit_weights"), ...axes],
        source_surface: "student_profile",
      });
      if (axes.length) {
        setProfile(p => ({ ...p, field_provenance: { ...(p.field_provenance || {}),
          ...Object.fromEntries(axes.map((a) => [a, { explicitly_answered: true, source_surface: "student_profile" }])) } }));
      }
      setDirty({});
      setTouchedAxes(new Set());
      setMessage({ kind: "ok", text: "Saved." });
      ok = true;
      // Refresh completeness (percent updates immediately)
      const c = await api.get("/profile/completeness");
      setMeta(c.data);
      // Refresh the journey ticket so it mirrors the saved profile.
      api.get("/ticket/me").then((r) => setTicket(r.data || null)).catch(() => {});
      // Broadcast so /shortlist re-ranks instantly.
      window.dispatchEvent(new CustomEvent("wua:profile-updated"));
    } catch (e) {
      if (Object.keys(extra).length) setDirty(d => ({ ...extra, ...d }));
      const detail = e?.response?.data?.detail;
      if (detail?.code === "parental_consent_required") {
        // Trigger the modal — remember which DOB the user was trying to save.
        setConsentModal({ age: detail.age, dobPending: changes.date_of_birth });
        setConsentStatus(null);
        setConsentError(null);
      } else {
        setMessage({ kind: "err", text: detail?.message || detail || "Save failed." });
      }
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
    return ok;
  };

  const sendParentEmail = async () => {
    if (!parentEmail.trim()) return;
    setConsentStatus("sending"); setConsentError(null);
    try {
      await api.post("/profile/parental-consent/request", { parent_email: parentEmail.trim() });
      setConsentStatus("sent");
    } catch (e) {
      setConsentStatus("failed");
      setConsentError(e?.response?.data?.detail?.message || "Could not send.");
    }
  };

  // Poll consent status while modal is open in "sent" state
  useEffect(() => {
    if (consentStatus !== "sent") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get("/profile/parental-consent/status");
        if (r.data?.granted) {
          setConsentStatus("granted");
          clearInterval(t);
        }
      } catch {}
    }, 4000);
    return () => clearInterval(t);
  }, [consentStatus]);

  const closeConsent = () => {
    setConsentModal(null); setParentEmail(""); setConsentStatus(null); setConsentError(null);
  };
  const retryDobSave = async () => {
    // Just retry save with the DOB kept in dirty.
    await save();
    closeConsent();
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6" data-testid="student-profile-loading">
        <div className="text-sm text-stone-500">Loading your profile…</div>
      </div>
    );
  }

  if (authGate) {
    return (
      <div className="max-w-xl mx-auto p-10 text-center" data-testid="student-profile-authgate">
        <h1 className="text-xl font-semibold text-stone-900 mb-2">
          Sign in to open your profile
        </h1>
        <p className="text-sm text-stone-600 mb-6">
          Your session has expired. Sign in again to carry on where you left off.
        </p>
        <button
          type="button"
          onClick={openSignIn}
          data-testid="student-profile-signin-btn"
          className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
        >
          Sign in
        </button>
      </div>
    );
  }

  const dirtyCount = new Set([...Object.keys(dirty), ...pendingKeys]).size;
  return (
    <div className="max-w-4xl mx-auto p-6 pb-24" data-testid="student-profile-page">
      <div className="flex items-center justify-between mb-6">
        <Link to="/shortlist" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900">
          <ArrowLeft className="w-4 h-4" />
          Back to shortlist
        </Link>
        <SaveBar message={message} saving={saving} dirtyCount={dirtyCount} onSave={save} tid="student-profile-save" msgTid="profile-save-message" />
        <UnsavedChangesGuard count={dirtyCount} saving={saving} onSave={save} />
      </div>
      <DraftBanner draft={draft} onRestore={restoreDraft} onDiscard={discardDraft} />

      <h1 className="text-2xl font-semibold text-stone-900 mb-1">Your student profile</h1>
      <p className="text-sm text-stone-600 mb-2 leading-relaxed">
        Fill this in progressively — each answer sharpens your matches and gets you ready for
        Fast Apply. Everything is private until you explicitly opt to share.
      </p>
      <Link
        to="/profile/consents"
        data-testid="profile-consents-link"
        className="inline-block text-[12px] text-emerald-700 hover:underline mb-4"
      >
        View & revoke your consent record →
      </Link>

      {ticket && (
        <div className="py-10 mb-6" data-testid="journey-ticket-wrapper">
          <MetroTicket
            data={ticket}
            publicUrl={ticket.ticket_id ? `/journey/${ticket.ticket_id}` : null}
            vertical={false}
            maxScale={1.3}
          />
        </div>
      )}

      {/* Overall progress bar */}
      {meta && (
        <div className="mb-8" data-testid="profile-overall-progress">
          <div className="flex items-center justify-between text-[12px] text-stone-600 mb-1">
            <span>Overall completeness</span>
            <span className="font-mono text-stone-800">{Math.round(meta.percent)}%</span>
          </div>
          <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all"
              style={{ width: `${meta.percent}%` }}
            />
          </div>
        </div>
      )}

      {SECTIONS.map(s => {
        const scored = meta?.sections?.find(x => x.key === s.key);
        return (
          <section key={s.key} id={`profile-section-${s.key}`} className="mb-8 border border-stone-200 rounded-xl bg-white p-5 scroll-mt-24"
                   data-testid={`profile-section-${s.key}`}>
            <div className="flex items-center gap-2 mb-1">
              {scored?.complete
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600" data-testid={`section-${s.key}-complete`} />
                : <Circle       className="w-4 h-4 text-stone-300" />
              }
              <h2 className="text-[15px] font-semibold text-stone-900">{s.title}</h2>
              {scored && (
                <span className="ml-auto text-[11px] text-stone-500 font-mono">
                  {scored.fields_filled}/{scored.fields_total}
                </span>
              )}
            </div>
            <p className="text-[12px] text-stone-500 mb-4">{s.subtitle}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {s.fields.filter(f => !f.showIf || f.showIf(profile)).map(f => (
                <FormField
                  key={f.key}
                  field={f}
                  value={profile[f.key]}
                  onChange={(v, axis) => setField(f.key, v, axis)}
                  onPending={(commit, preview) => setPending(f.key, commit, preview)}
                  profile={profile}
                />
              ))}
            </div>
          </section>
        );
      })}

      <div className="flex justify-end border-t border-stone-200 pt-5" data-testid="profile-bottom-save-bar">
        <SaveBar message={message} saving={saving} dirtyCount={dirtyCount} onSave={save} tid="student-profile-save-bottom" msgTid="profile-save-message-bottom" />
      </div>

      {consentModal && (
        <ParentalConsentModal
          age={consentModal.age}
          status={consentStatus}
          error={consentError}
          parentEmail={parentEmail}
          onEmailChange={setParentEmail}
          onSend={sendParentEmail}
          onRetry={retryDobSave}
          onClose={closeConsent}
        />
      )}
    </div>
  );
}


function ParentalConsentModal({ age, status, error, parentEmail, onEmailChange, onSend, onRetry, onClose }) {
  return (
    <div
      data-testid="parental-consent-modal"
      className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {status === "granted" ? (
          <>
            <div className="text-5xl mb-2" aria-hidden>✅</div>
            <h2 className="text-lg font-semibold text-stone-900 mb-1">Consent received</h2>
            <p className="text-sm text-stone-600 mb-4">
              Thanks — your parent has approved. We can now save your date of birth.
            </p>
            <button
              type="button"
              onClick={onRetry}
              data-testid="parental-consent-retry"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-md"
            >
              Save my date of birth
            </button>
          </>
        ) : status === "sent" ? (
          <>
            <div className="text-4xl mb-2" aria-hidden>📩</div>
            <h2 className="text-lg font-semibold text-stone-900 mb-1">Email sent</h2>
            <p className="text-sm text-stone-600 mb-4">
              We've sent a confirmation link to your parent. This page will update
              automatically once they click it. It's fine to close this window and
              come back later.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="parental-consent-close"
              className="w-full border border-stone-300 hover:bg-stone-50 text-sm px-4 py-2 rounded-md"
            >
              I'll come back later
            </button>
          </>
        ) : (
          <>
            <div className="text-4xl mb-2" aria-hidden>👨‍👩‍👧</div>
            <h2 className="text-lg font-semibold text-stone-900 mb-1">
              Ask a parent to confirm
            </h2>
            <p className="text-sm text-stone-600 mb-4 leading-relaxed">
              Because you're {age && `${age} `}under 18, we need a parent or guardian
              to approve before we store your date of birth. Enter their email and
              we'll send them a one-click confirmation link — no account needed.
            </p>
            <input
              type="email"
              required
              placeholder="parent@example.co.uk"
              value={parentEmail}
              onChange={e => onEmailChange(e.target.value)}
              data-testid="parental-consent-email-input"
              className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            {error && (
              <div data-testid="parental-consent-error" className="text-[12px] text-rose-700 mb-2">
                {error}
              </div>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onSend}
                disabled={!parentEmail.trim() || status === "sending"}
                data-testid="parental-consent-send"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white text-sm font-medium px-4 py-2 rounded-md"
              >
                {status === "sending" ? "Sending…" : "Send confirmation link"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-stone-500 hover:text-stone-800 px-3 py-2"
              >
                Cancel
              </button>
            </div>
            <p className="text-[11px] text-stone-500 mt-3 leading-relaxed">
              We never contact your parent again after this. The link expires in 7
              days and can only be used once.
            </p>
          </>
        )}
      </div>
    </div>
  );
}


function FormField({ field, value, onChange, onPending, profile }) {
  const [chipInput, setChipInput] = useState("");
  const isChips = field.type === "chips";
  useEffect(() => {
    if (!isChips) return;
    const text = chipInput.trim();
    const current = Array.isArray(value) ? value : (typeof value === "string" && value.trim()) ? [value.trim()] : [];
    onPending(text ? () => { setChipInput(""); return [...current, text]; } : null, text ? [...current, text] : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChips, chipInput, value]);
  const cls = "w-full border border-stone-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-500";

  const wrap = (inner, extraSpanCols = false) => (
    <label
      id={`profile-field-${field.key}`}
      className={field.type === "textarea" || field.type === "chips" || field.type === "fee_status_preview" || extraSpanCols ? "block sm:col-span-2 scroll-mt-24" : "block scroll-mt-24"}
    >
      <span className="text-[12px] font-medium text-stone-700 block mb-1">{field.label}</span>
      {inner}
      {field.hint && (
        <span className="block mt-1 text-[11px] text-stone-500 leading-snug">{field.hint}</span>
      )}
    </label>
  );

  if (field.type === "tests_not_taking") {
    return <TestsNotTakingField value={value} onChange={onChange} />;
  }
  if (field.type === "fit_weights") {
    return <FitWeightsField value={value} profile={profile} onChange={onChange} />;
  }
  if (field.type === "fee_status_preview") {
    return wrap(<FeeStatusPreview profile={profile} />);
  }

  if (field.type === "career_picker") {
    return wrap(
      <CareerPicker
        value={value || ""}
        onChange={onChange}
        placeholder={field.placeholder}
        testid={`profile-field-${field.key}`}
      />,
      true,
    );
  }

  if (field.type === "country_select") {
    return wrap(
      <select
        className={cls}
        value={value || ""}
        data-testid={`profile-field-${field.key}`}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{field.placeholder || "— select —"}</option>
        {COUNTRIES.map((c) => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
    );
  }

  if (field.type === "country_chips") {
    const chips = Array.isArray(value)
      ? value
      : (typeof value === "string" && value.trim())
        ? [value.trim()]
        : [];
    // Autocomplete + chip pattern — a plain input backed by a native
    // <datalist> gives the student a searchable country picker without
    // a combobox dependency, and we snap the free-text entry to a
    // known ISO-2 code on blur / Enter / exact-match-select so the
    // engine never sees junk — and critically so navigating away
    // doesn't lose a half-typed entry.
    const commitFrom = (raw) => {
      const text = (raw || "").trim();
      if (!text) return false;
      const upper = text.toUpperCase();
      const match =
        COUNTRIES.find((c) => c.value === upper) ||
        COUNTRIES.find((c) => c.label.toLowerCase() === text.toLowerCase()) ||
        COUNTRIES.find((c) => c.label.toLowerCase().includes(text.toLowerCase()));
      if (!match) return false;
      if (chips.includes(match.value)) { setChipInput(""); return true; }
      onChange([...chips, match.value]);
      setChipInput("");
      return true;
    };
    return wrap(
      <div>
        <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid={`profile-field-${field.key}-chips`}>
          {chips.map((code, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-[12px] px-2 py-0.5 rounded-full">
              {COUNTRY_LABEL[code] || code}
              <button
                type="button"
                onClick={() => onChange(chips.filter((_, idx) => idx !== i))}
                className="text-emerald-600 hover:text-emerald-900"
                aria-label={`Remove ${code}`}
              >×</button>
            </span>
          ))}
        </div>
        <input
          type="text"
          list={`country-dl-${field.key}`}
          className={cls}
          value={chipInput}
          placeholder={field.placeholder}
          data-testid={`profile-field-${field.key}`}
          onChange={(e) => {
            const raw = e.target.value;
            setChipInput(raw);
            // Auto-commit when the user clicks a datalist option — the
            // typed value will exactly equal a known country label.
            // Matches "United Kingdom" etc. We check case-sensitively
            // because the datalist fills the exact label from COUNTRIES.
            const exact = COUNTRIES.find((c) => c.label === raw);
            if (exact) commitFrom(raw);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commitFrom(chipInput);
            }
          }}
          onBlur={() => commitFrom(chipInput)}
        />
        <datalist id={`country-dl-${field.key}`}>
          {COUNTRIES.map((c) => (
            <option key={c.value} value={c.label} />
          ))}
        </datalist>
      </div>
    );
  }

  if (field.type === "currency_optional") {
    return <CurrencyOptionalField field={field} value={value} onChange={onChange} cls={cls} />;
  }
  if (field.type === "scholarship_gate") {
    return <ScholarshipGateField field={field} value={value} onChange={onChange} cls={cls} />;
  }
  if (field.type === "select") {
    return wrap(
      <select
        className={cls}
        value={value || ""}
        data-testid={`profile-field-${field.key}`}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">—</option>
        {field.options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (field.type === "boolean") {
    return wrap(
      <select
        className={cls}
        value={value === true ? "yes" : value === false ? "no" : ""}
        data-testid={`profile-field-${field.key}`}
        onChange={e => onChange(e.target.value === "yes" ? true : e.target.value === "no" ? false : null)}
      >
        <option value="">—</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    );
  }
  if (field.type === "slider") {
    return wrap(
      <div>
        <input
          type="range"
          min="1" max="5" step="1"
          value={value ?? 3}
          data-testid={`profile-field-${field.key}`}
          onChange={e => onChange(Number(e.target.value))}
          className="w-full"
        />
        <div className="text-[11px] text-stone-500 mt-0.5 font-mono">{value ?? 3} / 5 {(value ?? 3) === 3 && "(neutral)"}</div>
      </div>
    );
  }
  if (field.type === "predicted_grades") {
    return (
      <label className="block sm:col-span-2">
        <span className="text-[12px] font-medium text-stone-700 block mb-1">{field.label}</span>
        <PredictedGradesPicker
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          onPending={onPending}
          testIdPrefix={`profile-field-${field.key}`}
        />
      </label>
    );
  }
  if (field.type === "textarea") {
    return wrap(
      <div className="relative">
        <textarea
          rows={field.ai === "why_this_subject" ? 5 : 3}
          className={cls}
          value={value || ""}
          placeholder={field.placeholder}
          data-testid={`profile-field-${field.key}`}
          onChange={e => onChange(e.target.value)}
        />
        {field.ai && (
          <AiDraftButton
            field={field.ai}
            currentValue={value}
            onDraft={(text) => onChange(text)}
          />
        )}
      </div>
    );
  }
  if (field.type === "subject_chips") {
    const chips = Array.isArray(value)
      ? value
      : (typeof value === "string" && value.trim())
        ? [value.trim()]
        : [];
    return wrap(
      <SubjectChipPicker
        value={chips}
        onChange={(next) => onChange(next)}
        placeholder={field.placeholder}
        testid={`profile-field-${field.key}`}
      />
    );
  }
  if (field.type === "chips") {
    // Accept both an array (new shape) and a scalar string (legacy
    // single-value saves) so switching a `text` field to `chips` on
    // the schema side doesn't throw away the user's existing entry.
    const chips = Array.isArray(value)
      ? value
      : (typeof value === "string" && value.trim())
        ? [value.trim()]
        : [];
    return wrap(
      <div>
        <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid={`profile-field-${field.key}-chips`}>
          {chips.map((c, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-[12px] px-2 py-0.5 rounded-full">
              {c}
              <button
                type="button"
                onClick={() => onChange(chips.filter((_, idx) => idx !== i))}
                className="text-emerald-600 hover:text-emerald-900"
                aria-label={`Remove ${c}`}
              >×</button>
            </span>
          ))}
        </div>
        <input
          type="text"
          className={cls}
          value={chipInput}
          placeholder={field.placeholder}
          data-testid={`profile-field-${field.key}`}
          onChange={e => setChipInput(e.target.value)}
          onKeyDown={e => {
            if ((e.key === "Enter" || e.key === ",") && chipInput.trim()) {
              e.preventDefault();
              const next = [...chips, chipInput.trim()];
              onChange(next);
              setChipInput("");
            }
          }}
        />
      </div>
    );
  }
  // text / number / date
  return wrap(
    <input
      type={field.type}
      className={cls}
      value={value ?? ""}
      placeholder={field.placeholder}
      data-testid={`profile-field-${field.key}`}
      onChange={e => {
        const raw = e.target.value;
        if (field.type === "number") {
          onChange(raw ? Number(raw) : null);
        } else {
          onChange(raw);
        }
      }}
    />
  );
}


/**
 * Currency-aware tuition/living-cost input with a "Not sure yet"
 * opt-out. Stores/emits USD so backend & matching engine stay
 * unchanged; displays the value in the student's selected currency
 * (top-right navbar) so a UAE parent isn't converting AED → USD in
 * their head. Passing `0` = "no budget cap" for the engine.
 */
function CurrencyOptionalField({ field, value, onChange, cls }) {
  const { currency, rates } = useCurrency() || { currency: "USD", rates: { USD: 1 } };
  const rate = (rates && rates[currency]) || 1;
  const notSure = value === 0;
  const displayVal = value == null || notSure ? "" : Math.round(Number(value) * rate);
  const placeholderLocal = Math.round(20000 * rate);
  return (
    <label
      id={`profile-field-${field.key}`}
      className="block scroll-mt-24"
    >
      <span className="text-[12px] font-medium text-stone-700 block mb-1">
        {field.label} ({currency})
      </span>
      <input
        type="number"
        className={cls + (notSure ? " opacity-50" : "")}
        value={displayVal}
        disabled={notSure}
        placeholder={notSure ? "Any budget" : `e.g. ${placeholderLocal.toLocaleString()}`}
        data-testid={`profile-field-${field.key}`}
        onChange={e => {
          const local = e.target.value ? Number(e.target.value) : null;
          if (local === null || Number.isNaN(local)) return onChange(null);
          onChange(local / rate);
        }}
      />
      <label className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-stone-600 cursor-pointer select-none">
        <input
          type="checkbox"
          className="accent-emerald-600"
          checked={notSure}
          data-testid={`profile-field-${field.key}-notsure`}
          onChange={e => onChange(e.target.checked ? 0 : null)}
        />
        <span>I'm not sure yet — show me all budgets</span>
      </label>
    </label>
  );
}

function ScholarshipGateField({ field, value, onChange, cls }) {
  return (
    <label className="block sm:col-span-2">
      <span className="text-[12px] font-medium text-stone-700 block mb-1">{field.label}</span>
      <select
        className={cls}
        value={value === true ? "yes" : value === false ? "no" : ""}
        data-testid={`profile-field-${field.key}`}
        onChange={e => {
          const v = e.target.value;
          onChange(v === "yes" ? true : v === "no" ? false : null);
        }}
      >
        <option value="">—</option>
        <option value="no">No — I can proceed without one</option>
        <option value="yes">Yes — I need a scholarship to attend</option>
      </select>
      {value === true && (
        <div
          data-testid={`profile-field-${field.key}-warning`}
          className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2"
        >
          <AlertTriangle className="w-4 h-4 text-amber-700 flex-none mt-0.5" />
          <p className="text-[12px] text-amber-900 leading-snug">
            Heads-up: selecting <strong>Yes</strong> filters out universities we haven't verified as
            offering a scholarship that fits you. Many great courses still exist beyond that filter —
            only pick Yes if funding is a genuine dealbreaker.
          </p>
        </div>
      )}
    </label>
  );
}

/**
 * AiDraftButton
 * -------------
 * Small overlay button that lives inside a textarea's wrapper. Calls
 * `POST /profile/narrative/draft` with the field name and (if the
 * student has already typed something) uses that text as a hint so
 * the drafter steers toward their angle instead of ignoring it.
 *
 * Replaces the textarea content on success — the user is expected to
 * edit after. If the profile is too thin to draft specifically, the
 * backend returns a `profile_thin` 400 which we surface in-line.
 */
function AiDraftButton({ field, currentValue, onDraft }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const label = currentValue ? "Rewrite with AI" : "Help me draft";

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/profile/narrative/draft", {
        field,
        hint: (currentValue || "").slice(0, 400) || undefined,
      });
      if (data?.draft) onDraft(data.draft);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(d?.message || "Draft failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={run}
        disabled={busy}
        data-testid={`profile-ai-draft-${field}`}
        className="absolute top-2 right-2 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-900 hover:bg-amber-100 disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
        {busy ? "Drafting…" : label}
      </button>
      {error && (
        <div
          data-testid={`profile-ai-draft-${field}-error`}
          className="mt-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
        >
          {error}
        </div>
      )}
    </>
  );
}


/**
 * FeeStatusPreview
 * ----------------
 * Live preview of the inferred fee status at each of our 8 modelled
 * target countries. Hits `POST /fee-status/preview` with the current
 * (dirty + saved) profile every time the student edits a relevant
 * field — debounced 350 ms so Mongo doesn't get hammered on
 * keystrokes. Falls back to `GET /fee-status/me` on first mount so
 * returning users see a result before touching anything.
 */
function FeeStatusPreview({ profile }) {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);

  // The five fields that actually affect the engine's output. We
  // stringify these to form a stable debounce key so unrelated edits
  // (e.g. career_direction) don't trigger a re-query.
  const signalKey = JSON.stringify({
    n: profile?.nationalities || [],
    r: profile?.residence_country || "",
    y: profile?.residence_since_year || "",
    e: profile?.euss_status || "",
    t: !!profile?.uk_eea_resident_2020,
  });

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const payload = {
          nationalities:        profile?.nationalities || undefined,
          residence_country:    profile?.residence_country || undefined,
          residence_since_year: profile?.residence_since_year || undefined,
          euss_status:          profile?.euss_status || undefined,
          uk_eea_resident_2020: profile?.uk_eea_resident_2020 ?? undefined,
        };
        const { data } = await api.post("/fee-status/preview", payload);
        setData(data);
        setErr(null);
      } catch (e) {
        setErr(e?.response?.data?.detail?.message || "Preview unavailable.");
      }
    }, 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [signalKey]);

  if (err) return <div className="text-[12px] text-amber-700">{err}</div>;
  if (!data) return <div className="text-[12px] text-stone-500">Fill the fields above to see your status…</div>;
  const byCountry = data.by_country || {};
  const order = ["UK","IE","FR","DE","NL","US","CA","AU","SG","HK","JP","CN","IN","AE"];
  const pill = (s) => {
    if (s === "home")          return "bg-emerald-100 text-emerald-800 border border-emerald-200";
    if (s === "international") return "bg-stone-100 text-stone-700 border border-stone-200";
    if (s === "borderline")    return "bg-amber-100 text-amber-900 border border-amber-200";
    return "bg-stone-50 text-stone-500 border border-stone-200";
  };
  const label = (s) => s === "home" ? "Home fees"
                     : s === "international" ? "International"
                     : s === "borderline" ? "Borderline — check"
                     : "Unknown";
  return (
    <div data-testid="fee-status-preview" className="rounded-lg border border-stone-200 bg-stone-50/50 p-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {order.map(code => {
          const r = byCountry[code] || {};
          return (
            <div key={code} className="flex gap-3 items-start bg-white rounded-md border border-stone-200 p-2.5">
              <div className="shrink-0 min-w-[52px]">
                <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">{code}</div>
                <span
                  data-testid={`fee-status-${code}`}
                  className={`inline-block mt-0.5 text-[10.5px] font-medium px-1.5 py-0.5 rounded ${pill(r.status)}`}
                >
                  {label(r.status)}
                </span>
              </div>
              <div className="text-[11.5px] text-stone-600 leading-snug min-w-0">
                {r.reason || "—"}
                {Array.isArray(r.caveats) && r.caveats.length > 0 && (
                  <div className="mt-1 text-[10.5px] text-amber-800">
                    {r.caveats.join(" ")}
                  </div>
                )}
                {r.authority_url && (
                  <a
                    href={r.authority_url} target="_blank" rel="noopener noreferrer"
                    className="inline-block mt-1 text-[10.5px] text-[#4C1D95] underline underline-offset-2 hover:text-[#2E1065]"
                  >
                    official source ↗
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px] text-stone-500">
        <span>
          Guidelines only · the admitting university makes the final call.
        </span>
        <Link to="/methodology/fee-status" className="text-[#4C1D95] underline underline-offset-2 hover:text-[#2E1065]">
          See full methodology →
        </Link>
      </div>
    </div>
  );
}

